import React, { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { imageurl } from "../../../App";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import { RiskPanel } from "./OrderRisk";
import CourierPanel from "./CourierPanel";
import {
  FaArrowLeft,
  FaPrint,
  FaCheckCircle,
  FaTimesCircle,
  FaTruck,
  FaBoxOpen,
  FaUndo,
  FaMoneyBillWave,
  FaHistory,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaFileInvoiceDollar,
  FaExchangeAlt
} from "react-icons/fa";

export default function AdminOrderDetails() {
  const { id } = useParams();
  const history = useHistory();

  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [risk, setRisk] = useState(null);
  const [riskSaving, setRiskSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals
  const [openFulfillModal, setOpenFulfillModal] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [openReturnModal, setOpenReturnModal] = useState(false);

  // Fulfillment form
  const [courierName, setCourierName] = useState("Leopard Courier");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");

  // Refund form
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [restockInventory, setRestockInventory] = useState(true);

  // Return form
  const [returnReason, setReturnReason] = useState("Defective / Damaged");
  const [returnNotes, setReturnNotes] = useState("");

  const fetchOrderDetails = () => {
    setLoading(true);
    axios
      .post("/api/track-order", { tracking_query: id })
      .then((res) => {
        if (res.data.status === 200) {
          setOrder(res.data.order);
        } else {
          swal("Order Not Found", `Could not find order #${id}`, "error");
          history.push("/admin/orders");
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        swal("Error", "Failed to fetch order details", "error");
      });
  };

  const fetchTimeline = () => {
    axios.get(`/api/admin/orders/${id}/timeline`).then((res) => {
      if (res.data.status === 200) {
        setTimeline(res.data.timeline || []);
      }
    }).catch(() => {});
  };

  const recheckPayment = () => {
    setActionLoading(true);
    axios.post(`/api/admin/orders/${id}/payment-recheck`)
      .then((res) => {
        swal(res.data.state === "paid" ? "Paid" : "Payment", res.data.message || "Checked.", res.data.state === "paid" ? "success" : "info");
        fetchOrderDetails();
        fetchTimeline();
      })
      .catch(() => swal("Not checked", "Could not reach the payment company. Try again.", "error"))
      .finally(() => setActionLoading(false));
  };

  const fetchRisk = () => {
    axios.get(`/api/admin/orders/${id}/risk`).then((res) => {
      if (res.data.status === 200) setRisk(res.data.risk);
    }).catch(() => {});
  };

  const markRiskChecked = (checked) => {
    setRiskSaving(true);
    axios.post(`/api/admin/orders/${id}/risk-checked`, { checked })
      .then((res) => {
        if (res.data.status === 200) {
          setRisk(res.data.risk);
          fetchTimeline();
        } else {
          swal("Not saved", res.data.message || "Please try again.", "error");
        }
      })
      .catch(() => swal("Not saved", "Please try again.", "error"))
      .finally(() => setRiskSaving(false));
  };

  useEffect(() => {
    document.title = `Order #${id} — Admin Management`;
    fetchOrderDetails();
    fetchTimeline();
    fetchRisk();
  }, [id]);

  const isBankTransfer = order && (order.payment_mode === "BT" || order.payment_mode === "Bank Transfer");
  const slipPending = Boolean(isBankTransfer && order.payment_slip_status !== "accepted");
  const paymentAccepted = Boolean(isBankTransfer && order.payment_slip_status === "accepted");

  const handleAcceptPayment = () => {
    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/accept-payment`).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Payment accepted", res.data.message || "The transfer photo was accepted.", "success");
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Could not accept the photo", res.data.message || "Try again.", "error");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Could not accept the photo", err.response?.data?.message || "Try again.", "error");
    });
  };

  // Actions
  const handleConfirmOrder = () => {
    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/confirm`).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Order Confirmed", res.data.message || "Order marked as Confirmed for fulfillment", "success");
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Notice", res.data.message || "Failed to confirm", "info");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Error", err.response?.data?.message || "Error confirming order", "error");
    });
  };

  const handleFulfillSubmit = (e) => {
    e.preventDefault();
    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/fulfill`, {
      courier: courierName,
      tracking_number: trackingNumber || order.tracking_no,
      tracking_url: trackingUrl,
    }).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Shipment Dispatched", "Fulfillment record and tracking created", "success");
        setOpenFulfillModal(false);
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Notice", res.data.message || "Could not fulfill order", "info");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Error", err.response?.data?.message || "Error fulfilling order", "error");
    });
  };

  const handleDeliverOrder = () => {
    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/deliver`).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Order Delivered", "Delivery confirmed and revenue recognized in ledger", "success");
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Notice", res.data.message || "Could not mark delivered", "info");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Error", err.response?.data?.message || "Error delivering order", "error");
    });
  };

  const handleCancelOrder = () => {
    swal({
      title: "Cancel this Order?",
      text: "This will restore reserved stock to inventory and mark the order as Cancelled.",
      icon: "warning",
      buttons: ["Go Back", "Yes, Cancel Order"],
      dangerMode: true,
    }).then((willCancel) => {
      if (willCancel) {
        setActionLoading(true);
        axios.post(`/api/admin/orders/${order.id}/cancel`, { reason: "Admin Cancellation" }).then((res) => {
          setActionLoading(false);
          if (res.data.status === 200) {
            swal("Order Cancelled", "Order cancelled and stock released", "success");
            fetchOrderDetails();
            fetchTimeline();
          } else {
            swal("Notice", res.data.message || "Could not cancel", "info");
          }
        }).catch((err) => {
          setActionLoading(false);
          swal("Error", err.response?.data?.message || "Error cancelling order", "error");
        });
      }
    });
  };

  const handleReturnSubmit = (e) => {
    e.preventDefault();
    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/return`, {
      reason: returnReason,
      notes: returnNotes,
    }).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Return Logged", "Return request logged for parcel inspection", "success");
        setOpenReturnModal(false);
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Notice", res.data.message || "Could not create return", "info");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Error", err.response?.data?.message || "Error creating return", "error");
    });
  };

  const handleRefundSubmit = (e) => {
    e.preventDefault();
    if (!refundAmount || Number(refundAmount) <= 0) {
      swal("Invalid Amount", "Please enter a valid refund amount", "warning");
      return;
    }

    setActionLoading(true);
    axios.post(`/api/admin/orders/${order.id}/refund`, {
      amount: Number(refundAmount),
      reason: refundReason || "Customer Refund",
      restock_inventory: restockInventory,
      idempotency_key: `REFUND-UI-${order.id}-${Date.now()}`,
    }).then((res) => {
      setActionLoading(false);
      if (res.data.status === 200) {
        swal("Refund Processed", res.data.message || "Double-entry refund journal posted", "success");
        setOpenRefundModal(false);
        setRefundAmount("");
        setRefundReason("");
        fetchOrderDetails();
        fetchTimeline();
      } else {
        swal("Refund Failed", res.data.message || "Could not process refund", "error");
      }
    }).catch((err) => {
      setActionLoading(false);
      swal("Error", err.response?.data?.message || "Error processing refund", "error");
    });
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 fw-semibold">Loading Order Details #{id}...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-5 text-center">
        <h4>Order not found</h4>
        <Link to="/admin/orders" className="btn btn-dark mt-3">Back to Orders</Link>
      </div>
    );
  }

  const hasDate = (val) => Boolean(val && typeof val === "string" && !val.startsWith("0000") && val !== "null");
  const isCancelled = order.status_label === "Cancelled" || hasDate(order.order_cancel) || order.order_cancel == 1 || order.status == 6 || order.order_status === "Cancelled";
  const isDelivered = order.status_label === "Delivered" || hasDate(order.order_complete) || order.order_complete == 1 || order.status == 4 || order.order_status === "Delivered";
  const isShipped = order.status_label === "Shipped" || hasDate(order.order_dispatch) || order.order_dispatch == 1 || order.status == 3 || order.order_status === "Shipped" || order.fulfillment_status === "Fulfilled";
  const totalRefundedAmt = (order?.refunds || []).reduce((acc, r) => acc + Number(r.amount || 0), 0);
  const remainingRefundable = Math.max(0, Number(order?.total_amount || 0) - totalRefundedAmt);
  const isPartiallyRefunded = order.order_status === "Partially Refunded" || order.status_label === "Partially Refunded";
  const isRefunded = order.status_label === "Refunded" || order.status == 5 || order.order_status === "Refunded" || (totalRefundedAmt > 0 && remainingRefundable <= 0.01);

  const formatDateTime = (dateVal, options = { dateStyle: "short", timeStyle: "short" }) => {
    if (!dateVal || typeof dateVal !== "string" || dateVal.startsWith("0000")) return "—";
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-PK", options);
  };

  return (
    <div>
      {/* Top Header & Breadcrumbs */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <Link to="/admin/orders" className="btn btn-sm btn-light border rounded-pill px-3">
            <FaArrowLeft className="me-1" /> Orders
          </Link>
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h4 className="fw-bold mb-0 text-dark">Order #{order.id}</h4>
              <code className="px-2 py-1 bg-light border rounded fw-bold text-primary" style={{ fontSize: "13px" }}>
                {order.tracking_no}
              </code>
              <span className={`badge rounded-pill px-3 py-1 ${
                isCancelled ? "bg-danger text-white" :
                isRefunded ? "bg-danger text-white" :
                isPartiallyRefunded ? "bg-warning text-dark" :
                isDelivered ? "bg-success text-white" :
                isShipped ? "bg-primary text-white" : "bg-warning text-dark"
              }`}>
                {isRefunded ? "Refunded" : isPartiallyRefunded ? "Partially Refunded" : (order.status_label || "Active")}
              </span>
            </div>
            <small className="text-muted">
              Placed on {formatDateTime(order.created_at, { dateStyle: "medium", timeStyle: "short" })}
            </small>
          </div>
        </div>

        {/* Action Bar */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          {!isCancelled && !isDelivered && !isShipped && !isRefunded && !slipPending && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary fw-semibold rounded-pill px-3"
                disabled={actionLoading}
                onClick={handleConfirmOrder}
              >
                Confirm Order
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary fw-semibold rounded-pill px-3 d-flex align-items-center gap-1"
                disabled={actionLoading}
                onClick={() => {
                  setTrackingNumber(order.tracking_no);
                  setOpenFulfillModal(true);
                }}
              >
                <FaTruck size={12} /> Fulfill & Ship
              </button>
            </>
          )}

          {isShipped && !isDelivered && !isCancelled && !isRefunded && (
            <button
              type="button"
              className="btn btn-sm btn-success fw-semibold rounded-pill px-3 text-white d-flex align-items-center gap-1"
              disabled={actionLoading}
              onClick={handleDeliverOrder}
            >
              <FaCheckCircle size={12} /> Mark as Delivered
            </button>
          )}

          {isDelivered && (
            <button
              type="button"
              className="btn btn-sm btn-outline-dark fw-semibold rounded-pill px-3 d-flex align-items-center gap-1"
              onClick={() => setOpenReturnModal(true)}
            >
              <FaUndo size={11} /> Return & Inspect
            </button>
          )}

          {!isCancelled && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger fw-semibold rounded-pill px-3 d-flex align-items-center gap-1"
              onClick={() => {
                setRefundAmount(order.total_amount);
                setOpenRefundModal(true);
              }}
            >
              <FaMoneyBillWave size={12} /> Refund Order
            </button>
          )}

          {!isCancelled && !isDelivered && (
            <button
              type="button"
              className="btn btn-sm btn-light border text-danger fw-semibold rounded-pill px-3"
              disabled={actionLoading}
              onClick={handleCancelOrder}
            >
              Cancel Order
            </button>
          )}

          <Link
            to={`/admin/invoice-details/${order.id}`}
            className="btn btn-sm btn-dark rounded-pill px-3 d-flex align-items-center gap-1"
          >
            <FaFileInvoiceDollar size={12} /> Official Invoice
          </Link>
        </div>
      </div>

      {isBankTransfer && (
        <div className="admin-card mb-4">
          <div className="admin-card-header">
            <h5 className="admin-card-title mb-0">Payment photo</h5>
            <span className={`badge rounded-pill px-3 py-1 ${paymentAccepted ? "bg-success text-white" : "bg-warning text-dark"}`}>
              {paymentAccepted ? "Accepted" : "Waiting"}
            </span>
          </div>
          <div className="admin-card-body">
            <div className="payment-proof">
              <div>
                {order.payment_slip ? (
                  <a
                    className="payment-proof-frame"
                    href={`${imageurl}/${String(order.payment_slip).replace(/^\/+/, "")}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={`${imageurl}/${String(order.payment_slip).replace(/^\/+/, "")}`}
                      alt="Payment photo"
                    />
                  </a>
                ) : (
                  <div className="payment-proof-frame">
                    <span className="text-muted">No photo uploaded</span>
                  </div>
                )}
              </div>
              <div>
                <div className="payment-proof-kicker">Bank transfer</div>
                <p className="mb-0 text-muted" style={{ fontSize: "14px", lineHeight: 1.5 }}>
                  {paymentAccepted
                    ? "This photo was accepted. The transfer is recorded in the bank, and the order can be fulfilled."
                    : "Compare this photo with the order total, then accept it. The order stays placed until you do."}
                </p>
                <dl className="payment-proof-facts">
                  <dt>Order</dt>
                  <dd>#{order.id}</dd>
                  <dt>Amount</dt>
                  <dd>Rs. {Number(order.total_amount || 0).toLocaleString()}</dd>
                  <dt>Status</dt>
                  <dd>{paymentAccepted ? "Payment accepted" : "Photo waiting"}</dd>
                </dl>
                <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                  {slipPending && order.payment_slip && (
                    <button
                      type="button"
                      className="btn btn-sm btn-success text-white fw-semibold rounded-pill px-3"
                      disabled={actionLoading}
                      onClick={handleAcceptPayment}
                    >
                      Accept payment
                    </button>
                  )}
                  {order.payment_slip && (
                    <a
                      className="btn btn-sm btn-light border rounded-pill px-3"
                      href={`${imageurl}/${String(order.payment_slip).replace(/^\/+/, "")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open full photo
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Order Content */}
      <div className="row g-4">
        {/* Left Column: Items, Fulfillments, Financials, Timeline */}
        <div className="col-12 col-lg-8">
          {/* JazzCash / Easypaisa order not paid yet: it cannot be confirmed or shipped */}
          {["JAZZCASH", "EASYPAISA"].includes(order.payment_mode) && order.payment_status !== "Paid" && !order.order_cancel && (
            <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4" style={{ borderRadius: 12 }}>
              <div>
                <strong>Waiting for {order.payment_mode === "JAZZCASH" ? "JazzCash" : "Easypaisa"} payment.</strong>{" "}
                Do not ship yet. {order.payment_due_at ? `It is cancelled automatically if not paid by ${new Date(String(order.payment_due_at).replace(" ", "T")).toLocaleString()}.` : ""}
              </div>
              <button type="button" className="btn btn-sm btn-dark" onClick={recheckPayment} disabled={actionLoading}>Check payment now</button>
            </div>
          )}

          {/* Refusal-risk warning: call the customer before shipping */}
          <RiskPanel risk={risk} phone={order.phone} busy={riskSaving} onMarkChecked={markRiskChecked} />

          {/* Order Items Snapshot Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header d-flex justify-content-between align-items-center">
              <h5 className="admin-card-title mb-0">Purchased Items & Line Snapshots</h5>
              <span className="badge bg-light text-muted border">
                {order.orderitems?.length || 0} Line Item(s)
              </span>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light text-uppercase" style={{ fontSize: "11px" }}>
                  <tr>
                    <th>Product</th>
                    <th className="text-center">Real Price</th>
                    <th className="text-center">Discount</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Total (PKR)</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {order.orderitems?.map((item, idx) => {
                    const prod = item.products;
                    const paidPrice = Number(item.unit_price ?? item.price ?? 0);
                    const originalPrice = Number(item.original_price ?? item.productsprice?.seeling_price ?? paidPrice);
                    const hasDiscount = originalPrice > paidPrice && (originalPrice - paidPrice) > 0.01;
                    const lineDiscount = hasDiscount
                      ? Number(item.line_discount ?? ((originalPrice - paidPrice) * Number(item.qty || 1)))
                      : 0;
                    const lineTotal = Number(item.line_total ?? (paidPrice * Number(item.qty || 1)));
                    const unitCost = Number(prod?.cost_price || prod?.original_price || prod?.orignal_price || 0) > 0
                      ? Number(prod?.cost_price || prod?.original_price || prod?.orignal_price)
                      : Math.round(paidPrice * 0.6);

                    return (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <img
                              src={getProductImageUrl(prod?.image || prod?.product_image?.[0]?.image)}
                              alt={prod?.name}
                              style={{ width: "45px", height: "45px", objectFit: "contain", borderRadius: "6px", background: "#f8fafc", padding: "2px" }}
                              onError={handleProductImageError}
                            />
                            <div>
                              <span className="fw-bold text-dark d-block">
                                {item.display_name || prod?.name || `Item #${item.product_id}`}
                                {item.is_refunded ? (
                                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle ms-2 px-2 py-0.5" style={{ fontSize: "11px" }}>
                                    Refunded
                                  </span>
                                ) : isPartiallyRefunded ? (
                                  <span className="badge bg-light text-muted border ms-2 px-2 py-0.5" style={{ fontSize: "11px" }}>
                                    Active
                                  </span>
                                ) : null}
                              </span>
                              <small className="text-muted">SKU: {item.variant?.sku || `SKU-${item.product_id}`} • Unit Cost: Rs. {unitCost.toLocaleString()}</small>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          {hasDiscount ? (
                            <div>
                              <span className="fw-bold text-dark d-block">Rs. {paidPrice.toLocaleString()}</span>
                              <del className="text-muted" style={{ fontSize: "11px" }}>
                                Rs. {originalPrice.toLocaleString()}
                              </del>
                            </div>
                          ) : (
                            <span className="fw-semibold text-dark">Rs. {originalPrice.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="text-center">
                          {hasDiscount ? (
                            <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1" style={{ fontSize: "11px" }}>
                              -Rs. {lineDiscount.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center fw-bold">{item.qty}</td>
                        <td className="text-end">
                          <span className="fw-bold text-dark d-block">Rs. {lineTotal.toLocaleString()}</span>
                          {hasDiscount && (
                            <del className="text-muted" style={{ fontSize: "11px" }}>
                              Rs. {(originalPrice * Number(item.qty || 1)).toLocaleString()}
                            </del>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chronological Event Timeline */}
          <div className="admin-card mb-4">
            <div className="admin-card-header d-flex justify-content-between align-items-center bg-white py-3">
              <h5 className="admin-card-title mb-0 d-flex align-items-center gap-2">
                <FaHistory className="text-primary" /> Chronological Event Timeline
              </h5>
              <span className="badge bg-light text-dark border px-2 py-1">
                {timeline.length} {timeline.length === 1 ? "Event" : "Events"} Logged
              </span>
            </div>
            <div className="admin-card-body p-4">
              {timeline.length === 0 ? (
                <div className="text-center text-muted py-3">No timeline events recorded yet.</div>
              ) : (
                <div className="position-relative ps-4" style={{ borderLeft: "2px solid #e2e8f0" }}>
                  {timeline.map((event, idx) => {
                    const isRefund = event.event_type === "REFUND_PROCESSED" || event.event_type === "ORDER_REFUNDED" || event.metadata?.refund_no;
                    const isCompleted = event.event_type === "ORDER_COMPLETED" || event.event_type === "ORDER_DELIVERED";
                    const isDispatched = event.event_type === "ORDER_DISPATCHED" || event.event_type === "ORDER_FULFILLED";
                    const isCancelled = event.event_type === "ORDER_CANCELLED";

                    const markerColor = isRefund ? "#ef4444" : isCompleted ? "#10b981" : isDispatched ? "#f59e0b" : isCancelled ? "#6b7280" : "#2563eb";

                    return (
                      <div key={idx} className="mb-4 position-relative">
                        {/* Dot Marker */}
                        <div
                          style={{
                            position: "absolute",
                            left: "-25px",
                            top: "4px",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: markerColor,
                            border: "3px solid #ffffff",
                            boxShadow: `0 0 0 2px ${markerColor}33`,
                          }}
                        ></div>

                        <div className="d-flex justify-content-between align-items-start mb-1 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                              {event.title}
                            </span>
                            {isRefund && (
                              <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle fw-semibold" style={{ fontSize: "11px" }}>
                                <FaUndo className="me-1" size={9} />
                                {event.metadata?.refund_type || (event.metadata?.is_partial ? "Partial Refund" : "Refund Disbursed")}
                              </span>
                            )}
                            {isCompleted && (
                              <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle fw-semibold" style={{ fontSize: "11px" }}>
                                Delivered
                              </span>
                            )}
                          </div>
                          <small className="text-muted" style={{ fontSize: "11.5px" }}>
                            {formatDateTime(event.created_at, { dateStyle: "medium", timeStyle: "short" })}
                          </small>
                        </div>

                        <p className="text-muted mb-1" style={{ fontSize: "12.5px" }}>
                          {event.description}
                        </p>

                        {/* Rich Refund Details Card */}
                        {isRefund && (
                          <div className="p-3 my-2 rounded-3 border bg-light bg-opacity-50" style={{ borderColor: "#fecaca" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 pb-2 border-bottom">
                              <div>
                                <span className="text-muted small d-block">Refund Transaction Reference:</span>
                                <span className="fw-bold text-dark font-monospace">
                                  {event.metadata?.refund_no || "REF-TRANSACTION"}
                                </span>
                              </div>
                              <div className="text-end">
                                <span className="text-muted small d-block">Disbursed Amount:</span>
                                <span className="fw-bold fs-6 text-danger font-monospace">
                                  Rs. {Number(event.metadata?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>

                            <div className="row g-2 mb-2 small">
                              <div className="col-12 col-md-6">
                                <span className="text-muted">Reason / Memo: </span>
                                <span className="fw-semibold text-dark">
                                  {event.metadata?.reason || "Customer Return / Admin Refund"}
                                </span>
                              </div>
                              <div className="col-12 col-md-6 text-md-end">
                                <span className="text-muted">Inventory Action: </span>
                                {event.metadata?.restock_inventory !== false ? (
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle">
                                    Restocked to Warehouse
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary bg-opacity-10 text-secondary border">
                                    No Restock
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Refunded Items Breakdown */}
                            {Array.isArray(event.metadata?.items) && event.metadata.items.length > 0 && (
                              <div className="mt-2 pt-2 border-top">
                                <span className="text-muted small fw-semibold d-block mb-1">
                                  Refunded Items Affected:
                                </span>
                                <div className="d-flex flex-column gap-1">
                                  {event.metadata.items.map((item, itmIdx) => (
                                    <div
                                      key={itmIdx}
                                      className="d-flex justify-content-between align-items-center bg-white px-2 py-1 rounded border small"
                                    >
                                      <span className="fw-medium text-dark">
                                        {item.product_name || `Item #${item.product_id || itmIdx + 1}`}
                                        {item.qty ? ` × ${item.qty}` : ""}
                                      </span>
                                      <span className="fw-semibold text-danger font-monospace">
                                        Rs. {Number(item.total_price || item.amount || 0).toLocaleString()}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Ledger & Remaining Info */}
                            <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center flex-wrap gap-2 text-muted" style={{ fontSize: "11px" }}>
                              <span className="d-flex align-items-center gap-1">
                                ⚖️ {event.metadata?.ledger_reconciliation || "Balanced Double-Entry Journal Posted"}
                              </span>
                              {event.metadata?.remaining_refundable !== undefined && (
                                <span className="badge bg-white text-muted border">
                                  Remaining Refundable: Rs. {Number(event.metadata.remaining_refundable).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-1 d-flex align-items-center gap-2">
                          <small className="badge bg-light text-muted border" style={{ fontSize: "10.5px" }}>
                            Logged by {event.actor_name || "System"} ({event.actor_type || "SYSTEM"})
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Financial Summary, Customer & Address */}
        <div className="col-12 col-lg-4">
          {/* Courier booking, or where the parcel is now */}
          <CourierPanel orderId={order.id} onChanged={() => { fetchOrderDetails(); fetchTimeline(); }} />

          {/* Financial Breakdown Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header d-flex justify-content-between align-items-center">
              <h5 className="admin-card-title mb-0">Financial Summary</h5>
              <span className={`badge rounded-pill px-2 py-1 ${paymentAccepted || isDelivered ? "bg-success text-white" : "bg-warning text-dark"}`}>
                {isBankTransfer ? "Bank transfer" : order.payment_mode} • {paymentAccepted ? "Payment accepted" : isDelivered ? "Paid" : slipPending ? "Photo waiting" : "Payment Pending"}
              </span>
            </div>
            <div className="admin-card-body p-3" style={{ fontSize: "13.5px" }}>
              {(() => {
                const realSubtotal = Number(order.original_amount || (Number(order.total_amount) + Number(order.total_discount || 0)));
                const catalogDisc = Number(order.catalog_discount || (Number(order.total_discount || 0) - Number(order.coupon_discount || 0)));
                const paidSubtotal = Number(order.items_subtotal || (realSubtotal - catalogDisc));
                const couponDisc = Number(order.coupon_discount || 0);
                const totalSavings = catalogDisc + couponDisc;

                return (
                  <>
                    <div className="d-flex justify-content-between py-1 text-muted">
                      <span>Items Subtotal (Real Price):</span>
                      <span className="fw-semibold text-dark font-monospace">Rs. {realSubtotal.toLocaleString()}</span>
                    </div>

                    {catalogDisc > 0 && (
                      <div className="d-flex justify-content-between py-1 text-success">
                        <span>Catalog Discount:</span>
                        <span className="fw-semibold font-monospace">-Rs. {catalogDisc.toLocaleString()}</span>
                      </div>
                    )}

                    {catalogDisc > 0 && (
                      <div className="d-flex justify-content-between py-1 text-muted border-top border-bottom my-1 bg-light px-2 rounded">
                        <span className="fw-semibold">Items Subtotal (Discounted):</span>
                        <span className="fw-bold text-dark font-monospace">Rs. {paidSubtotal.toLocaleString()}</span>
                      </div>
                    )}

                    {couponDisc > 0 && (
                      <div className="d-flex justify-content-between py-1 text-danger">
                        <span>Coupon Discount ({order.coupon_code || "COUPON"}):</span>
                        <span className="fw-semibold font-monospace">-Rs. {couponDisc.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between py-1 text-muted">
                      <span>Nationwide Shipping:</span>
                      <span className="fw-semibold text-dark font-monospace">
                        {Number(order.shipping_fee || 0) > 0 ? `Rs. ${Number(order.shipping_fee).toLocaleString()}` : "FREE"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between py-1 text-muted">
                      <span>Estimated Sales Tax (0%):</span>
                      <span className="font-monospace">Rs. 0.00</span>
                    </div>

                    <hr className="my-2" />

                    <div className="d-flex justify-content-between align-items-baseline fs-5 fw-bold text-primary">
                      <span>Grand Total:</span>
                      <span className="font-monospace">Rs. {Number(order.total_amount).toLocaleString()}</span>
                    </div>

                    {totalSavings > 0 && (
                      <div className="d-flex justify-content-between pt-1 text-success fw-bold" style={{ fontSize: "12.5px" }}>
                        <span>Total Customer Savings:</span>
                        <span className="font-monospace">Rs. {totalSavings.toLocaleString()} OFF</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between pt-2 text-muted" style={{ fontSize: "12px" }}>
                      <span>Remaining Refundable:</span>
                      <span className="fw-semibold text-success font-monospace">
                        Rs. {Number(order.refundable_amount || order.total_amount).toLocaleString()}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Customer Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header">
              <h5 className="admin-card-title mb-0">Customer Details</h5>
            </div>
            <div className="admin-card-body p-3" style={{ fontSize: "13px" }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="admin-user-avatar"><FaUser size={12} /></div>
                <div>
                  <h6 className="fw-bold mb-0 text-dark">{order.firstname} {order.lastname}</h6>
                  <small className="text-muted">{order.user_id ? "Registered User" : "Guest Shopper"}</small>
                </div>
              </div>
              <div className="d-flex flex-column gap-1 text-muted">
                <div className="d-flex align-items-center gap-2"><FaEnvelope size={11} /> <span>{order.email}</span></div>
                <div className="d-flex align-items-center gap-2"><FaPhone size={11} /> <span>{order.phone}</span></div>
              </div>
            </div>
          </div>

          {/* Shipping Address Card */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h5 className="admin-card-title mb-0">Shipping Address</h5>
            </div>
            <div className="admin-card-body p-3 text-muted" style={{ fontSize: "13px" }}>
              <div className="d-flex gap-2">
                <FaMapMarkerAlt size={13} className="mt-1 flex-shrink-0 text-primary" />
                <div>
                  <p className="mb-1 fw-semibold text-dark">{order.address}</p>
                  <p className="mb-0 text-muted">{order.city}{order.state ? `, ${order.state}` : ""}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fulfill Modal */}
      {openFulfillModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold" style={{ fontSize: "16px" }}>Fulfill & Dispatch Order #{order.id}</h5>
                <button type="button" className="btn-close" onClick={() => setOpenFulfillModal(false)}></button>
              </div>
              <form onSubmit={handleFulfillSubmit}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Courier / Logistics Partner</label>
                    <select className="form-select" value={courierName} onChange={(e) => setCourierName(e.target.value)}>
                      <option value="Leopard Courier">Leopard Courier Services</option>
                      <option value="TCS Logistics">TCS Express Logistics</option>
                      <option value="M&P Courier">M&P Express</option>
                      <option value="Call Courier">Call Courier</option>
                      <option value="Trax Courier">Trax Express</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Courier Tracking Number</label>
                    <input
                      type="text"
                      required
                      className="form-control fw-bold"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Tracking URL (Optional)</label>
                    <input
                      type="url"
                      placeholder="https://track.courier.com/..."
                      className="form-control"
                      value={trackingUrl}
                      onChange={(e) => setTrackingUrl(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer border-top bg-light">
                  <button type="button" className="btn btn-sm btn-light border" onClick={() => setOpenFulfillModal(false)}>Cancel</button>
                  <button type="submit" disabled={actionLoading} className="btn btn-sm btn-primary fw-semibold px-4">
                    {actionLoading ? "Dispatching..." : "Confirm & Dispatch Shipment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {openReturnModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold" style={{ fontSize: "16px" }}>Log Customer Return Request</h5>
                <button type="button" className="btn-close" onClick={() => setOpenReturnModal(false)}></button>
              </div>
              <form onSubmit={handleReturnSubmit}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Return Reason</label>
                    <select className="form-select" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                      <option value="Defective / Damaged Item">Defective / Damaged Item</option>
                      <option value="Incorrect Item Received">Incorrect Item Received</option>
                      <option value="Customer Dissatisfaction">Customer Dissatisfaction</option>
                      <option value="Size / Variant Exchange">Size / Variant Exchange</option>
                    </select>
                  </div>
                  <div className="mb-0">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Customer Notes</label>
                    <textarea
                      rows="3"
                      className="form-control"
                      placeholder="Add inspection or return notes..."
                      value={returnNotes}
                      onChange={(e) => setReturnNotes(e.target.value)}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer border-top bg-light">
                  <button type="button" className="btn btn-sm btn-light border" onClick={() => setOpenReturnModal(false)}>Cancel</button>
                  <button type="submit" disabled={actionLoading} className="btn btn-sm btn-dark fw-semibold px-4">
                    {actionLoading ? "Logging..." : "Create Return Request"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {openRefundModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold" style={{ fontSize: "16px" }}>Issue Double-Entry Refund — Order #{order.id}</h5>
                <button type="button" className="btn-close" onClick={() => setOpenRefundModal(false)}></button>
              </div>
              <form onSubmit={handleRefundSubmit}>
                <div className="modal-body p-4">
                  <div className="alert alert-warning py-2 mb-3" style={{ fontSize: "12.5px" }}>
                    <strong>Financial Note:</strong> Processing a refund posts balanced journal reversals (Contra-Revenue & Cash/Receivable adjustment) in the General Ledger.
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Refund Amount (PKR)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      min="1"
                      max={remainingRefundable}
                      className="form-control fw-bold fs-5 text-danger"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                    />
                    <div className="d-flex justify-content-between mt-1 text-muted small">
                      <span>Max Refundable: <strong className="text-dark">Rs. {remainingRefundable.toLocaleString()}</strong></span>
                      {totalRefundedAmt > 0 && <span>Already Refunded: <span className="text-danger fw-semibold">Rs. {totalRefundedAmt.toLocaleString()}</span></span>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>Refund Reason</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Defective item, customer return, price adjustment..."
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                    />
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="restockCheck"
                      checked={restockInventory}
                      onChange={(e) => setRestockInventory(e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="restockCheck" style={{ fontSize: "13px" }}>
                      Restock Returned Inventory (Reverses COGS & Restores Stock)
                    </label>
                  </div>
                </div>
                <div className="modal-footer border-top bg-light">
                  <button type="button" className="btn btn-sm btn-light border" onClick={() => setOpenRefundModal(false)}>Cancel</button>
                  <button type="submit" disabled={actionLoading || remainingRefundable <= 0} className="btn btn-sm btn-danger fw-semibold px-4">
                    {remainingRefundable <= 0 ? "Order Fully Refunded" : actionLoading ? "Processing Reversal..." : "Confirm & Post Refund"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
